import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import Ico from './ui/Ico';

export default function PipelineIntelligence({ api }) {
  const [insights, setInsights] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [rottingDeals, setRottingDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPipeline, setSelectedPipeline] = useState('all');
  const [pipelines, setPipelines] = useState([]);
  const [activeTab, setActiveTab] = useState('insights');
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [dealScore, setDealScore] = useState(null);

  useEffect(() => {
    fetchData();
    fetchPipelines();
  }, [selectedPipeline]);

  const fetchPipelines = async () => {
    try {
      const response = await api.get('/pipelines');
      setPipelines(response.data?.items || []);
    } catch (error) {
      console.error('Error fetching pipelines:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [insightsRes, alertsRes, rottingRes] = await Promise.all([
        api.get(`/pipeline/insights${selectedPipeline && selectedPipeline !== 'all' ? `?pipeline_id=${selectedPipeline}` : ''}`),
        api.get('/deals/alerts'),
        api.get('/pipeline/rotting-deals?days_threshold=14')
      ]);

      setInsights(insightsRes.data);
      setAlerts(alertsRes.data);
      setRottingDeals(rottingRes.data);
    } catch (error) {
      console.error('Error fetching pipeline intelligence data:', error);
      toast.error('Erro ao carregar dados de inteligência do pipeline');
    } finally {
      setLoading(false);
    }
  };

  const handleAdvanceDeal = async (dealId, force = false) => {
    try {
      const response = await api.post(`/deals/${dealId}/auto-advance?force=${force}`);
      toast.success(response.data.message);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error advancing deal:', error);
      toast.error('Erro ao avançar negócio');
    }
  };

  const handleCalculateScore = async (dealId) => {
    try {
      const response = await api.get(`/deals/${dealId}/score`);
      setDealScore(response.data);
      setShowScoreModal(true);
    } catch (error) {
      console.error('Error calculating deal score:', error);
      toast.error('Erro ao calcular score do negócio');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-blue-100 text-blue-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Intelligence do Pipeline</h1>
            <p className="text-slate-600 mt-1">Automação e otimização inteligente</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-16 bg-slate-200 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Intelligence do Pipeline</h1>
          <p className="text-slate-600 mt-1">Automação e otimização inteligente de vendas</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Select value={selectedPipeline} onValueChange={setSelectedPipeline}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Todos os pipelines" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os pipelines</SelectItem>
              {pipelines.map((pipeline) => (
                <SelectItem key={pipeline.id} value={pipeline.id || `pipeline-${Date.now()}`}>
                  {pipeline.name || 'Pipeline sem nome'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button 
            onClick={fetchData}
            variant="outline"
            className="border-orange-300 text-orange-600 hover:bg-orange-50"
          >
            <Ico e="🔄" />Atualizar
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      {insights && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Total de Negócios</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {insights.total_deals}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Pipeline ativo
                  </p>
                </div>
                <div className="h-12 w-12 bg-gradient-to-r from-blue-100 to-blue-200 rounded-full flex items-center justify-center">
                  <span className="text-xl"><Ico e="📊" bare /></span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Negócios Parados</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {insights.stagnant_deals.length}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Sem atividade 7+ dias
                  </p>
                </div>
                <div className="h-12 w-12 bg-gradient-to-r from-orange-100 to-orange-200 rounded-full flex items-center justify-center">
                  <span className="text-xl"><Ico e="⚠" bare /></span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Alta Prioridade</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {insights.high_priority_deals.length}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Alto valor/probabilidade
                  </p>
                </div>
                <div className="h-12 w-12 bg-gradient-to-r from-purple-100 to-purple-200 rounded-full flex items-center justify-center">
                  <span className="text-xl"><Ico e="🎯" bare /></span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Precisam Atenção</p>
                  <p className="text-2xl font-bold text-red-600">
                    {insights.deals_needing_attention.length}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Ação necessária
                  </p>
                </div>
                <div className="h-12 w-12 bg-gradient-to-r from-red-100 to-red-200 rounded-full flex items-center justify-center">
                  <span className="text-xl"><Ico e="🚨" bare /></span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="insights">Insights</TabsTrigger>
          <TabsTrigger value="alerts">Alertas</TabsTrigger>
          <TabsTrigger value="rotting">Deals Parados</TabsTrigger>
          <TabsTrigger value="automation">Automação</TabsTrigger>
          <TabsTrigger value="concorrencia"><Ico e="⚔" />Concorrência</TabsTrigger>
          <TabsTrigger value="radar"><Ico e="🎯" />Radar de Risco</TabsTrigger>
        </TabsList>

        <TabsContent value="insights" className="space-y-6 mt-6">
          {insights && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Stage Analysis */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <span><Ico e="⏱" bare /></span>
                    <span>Tempo Médio por Estágio</span>
                  </CardTitle>
                  <CardDescription>Análise de duração em cada etapa</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(insights.avg_stage_duration).map(([stage, duration]) => (
                      <div key={stage} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{stage}</p>
                          <p className="text-sm text-slate-600">
                            {insights.conversion_rates[stage]?.toFixed(1)}% dos negócios
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-blue-600">
                            {duration.toFixed(0)} dias
                          </p>
                          {insights.bottleneck_stages.includes(stage) && (
                            <Badge className="bg-red-100 text-red-800 text-xs">
                              Gargalo
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Bottlenecks */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <span><Ico e="🔍" bare /></span>
                    <span>Análise de Gargalos</span>
                  </CardTitle>
                  <CardDescription>Estágios que precisam de otimização</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {insights.bottleneck_stages.length > 0 ? (
                      insights.bottleneck_stages.map((stage) => (
                        <div key={stage} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-red-900">{stage}</h4>
                            <Badge className="bg-red-100 text-red-800">
                              Gargalo Identificado
                            </Badge>
                          </div>
                          <p className="text-sm text-red-700">
                            Duração média: {insights.avg_stage_duration[stage]?.toFixed(0)} dias
                          </p>
                          <div className="mt-3 space-y-1">
                            <p className="text-xs font-medium text-red-800">Recomendações:</p>
                            <ul className="text-xs text-red-700 space-y-1">
                              <li>• Revisar critérios de avanço para este estágio</li>
                              <li>• Automatizar tarefas repetitivas</li>
                              <li>• Treinar equipe em melhores práticas</li>
                            </ul>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center p-6 text-slate-500">
                        <span className="text-2xl mb-2 block"><Ico e="🎉" bare /></span>
                        <p>Nenhum gargalo crítico identificado!</p>
                        <p className="text-sm mt-1">Pipeline funcionando de forma eficiente</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="alerts" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <span><Ico e="🚨" bare /></span>
                <span>Alertas do Pipeline</span>
                <Badge className="ml-2">{alerts.length} alertas</Badge>
              </CardTitle>
              <CardDescription>Negócios que precisam de atenção imediata</CardDescription>
            </CardHeader>
            <CardContent>
              {alerts.length > 0 ? (
                <div className="space-y-3">
                  {alerts.map((alert, index) => (
                    <div key={index} className="p-4 border rounded-lg hover:shadow-sm transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <Badge className={getSeverityColor(alert.severity)}>
                              {alert.severity === 'critical' ? <><Ico e="🔴" />Crítico</> :
                               alert.severity === 'high' ? <><Ico e="🟠" />Alto</> :
                               alert.severity === 'medium' ? <><Ico e="🟡" />Médio</> : <><Ico e="🔵" />Baixo</>}
                            </Badge>
                            <Badge variant="outline">
                              {alert.alert_type === 'stagnant' ? 'Parado' :
                               alert.alert_type === 'overdue' ? 'Atrasado' :
                               alert.alert_type === 'high_value' ? 'Alto Valor' : 'Baixa Atividade'}
                            </Badge>
                          </div>
                          <p className="text-slate-900 font-medium mb-1">{alert.message}</p>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          <Button 
                            size="sm"
                            variant="outline"
                            onClick={() => handleCalculateScore(alert.deal_id)}
                          >
                            <Ico e="📊" />Score
                          </Button>
                          <Button 
                            size="sm"
                            onClick={() => handleAdvanceDeal(alert.deal_id)}
                            className="bg-blue-100 text-blue-700 hover:bg-blue-200"
                          >
                            <Ico e="🚀" />Avançar
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center p-8 text-slate-500">
                  <span className="text-4xl mb-4 block"><Ico e="✅" bare /></span>
                  <h3 className="text-lg font-semibold mb-2">Nenhum alerta ativo</h3>
                  <p>Todos os negócios estão funcionando normalmente</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rotting" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <span><Ico e="🕐" bare /></span>
                <span>Deals Parados</span>
                <Badge className="ml-2">{rottingDeals.deals?.length || 0} deals</Badge>
              </CardTitle>
              <CardDescription>
                Negócios sem progresso há mais de {rottingDeals.threshold_days} dias
              </CardDescription>
            </CardHeader>
            <CardContent>
              {rottingDeals.deals && rottingDeals.deals.length > 0 ? (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="text-center p-3 bg-red-50 rounded-lg">
                      <p className="text-xl font-bold text-red-600">{rottingDeals.summary.critical}</p>
                      <p className="text-sm text-slate-600">Críticos</p>
                    </div>
                    <div className="text-center p-3 bg-orange-50 rounded-lg">
                      <p className="text-xl font-bold text-orange-600">{rottingDeals.summary.high}</p>
                      <p className="text-sm text-slate-600">Alto Risco</p>
                    </div>
                    <div className="text-center p-3 bg-yellow-50 rounded-lg">
                      <p className="text-xl font-bold text-yellow-600">{rottingDeals.summary.medium}</p>
                      <p className="text-sm text-slate-600">Médio Risco</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <p className="text-xl font-bold text-slate-600">
                        {formatCurrency(rottingDeals.summary.total_value_at_risk)}
                      </p>
                      <p className="text-sm text-slate-600">Valor em Risco</p>
                    </div>
                  </div>

                  {/* Deals List */}
                  <div className="space-y-3">
                    {rottingDeals.deals.map((deal) => (
                      <div key={deal.deal_id} className="p-4 border rounded-lg hover:shadow-sm transition-shadow">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h4 className="font-medium text-slate-900">{deal.title}</h4>
                              <Badge className={getSeverityColor(deal.severity)}>
                                Score: {deal.rot_score}
                              </Badge>
                            </div>
                            <div className="grid grid-cols-4 gap-4 text-sm text-slate-600">
                              <div>
                                <span className="font-medium">Estágio:</span> {deal.stage}
                              </div>
                              <div>
                                <span className="font-medium">Valor:</span> {formatCurrency(deal.value)}
                              </div>
                              <div>
                                <span className="font-medium">Dias no estágio:</span> {deal.days_in_stage}
                              </div>
                              <div>
                                <span className="font-medium">Sem atividade:</span> {deal.days_since_activity} dias
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => handleCalculateScore(deal.deal_id)}
                            >
                              <Ico e="📊" />Analisar
                            </Button>
                            <Button 
                              size="sm"
                              onClick={() => handleAdvanceDeal(deal.deal_id, true)}
                              className="bg-orange-100 text-orange-700 hover:bg-orange-200"
                            >
                              <Ico e="🚀" />Forçar Avanço
                            </Button>
                          </div>
                        </div>
                        
                        {deal.recommended_actions.length > 0 && (
                          <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                            <p className="text-sm font-medium text-blue-900 mb-1">Ações Recomendadas:</p>
                            <ul className="text-sm text-blue-700 space-y-1">
                              {deal.recommended_actions.map((action, index) => (
                                <li key={index}>• {action}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center p-8 text-slate-500">
                  <span className="text-4xl mb-4 block"><Ico e="🎉" bare /></span>
                  <h3 className="text-lg font-semibold mb-2">Pipeline saudável!</h3>
                  <p>Nenhum negócio identificado como "parado"</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="automation" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <span><Ico e="🤖" bare /></span>
                <span>Automação Inteligente</span>
              </CardTitle>
              <CardDescription>Ferramentas de automação para otimizar o pipeline</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                  <div className="flex items-center space-x-3 mb-4">
                    <span className="text-2xl"><Ico e="🎯" bare /></span>
                    <div>
                      <h3 className="font-semibold text-slate-900">Auto-Scoring</h3>
                      <p className="text-sm text-slate-600">Pontuação automática de oportunidades</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-700 mb-4">
                    Algoritmo inteligente que analisa múltiplos fatores para calcular a probabilidade de fechamento.
                  </p>
                  <Button 
                    className="w-full"
                    onClick={async () => {
                      try {
                        // Calculate scores for all deals (implementation needed)
                        toast.success('Cálculo de score iniciado para todos os negócios');
                      } catch (error) {
                        toast.error('Erro ao calcular scores');
                      }
                    }}
                  >
                    <Ico e="📊" />Calcular Scores
                  </Button>
                </div>

                <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                  <div className="flex items-center space-x-3 mb-4">
                    <span className="text-2xl"><Ico e="🚀" bare /></span>
                    <div>
                      <h3 className="font-semibold text-slate-900">Avanço Automático</h3>
                      <p className="text-sm text-slate-600">Progressão inteligente de estágios</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-700 mb-4">
                    Move automaticamente negócios qualificados para o próximo estágio com base em critérios definidos.
                  </p>
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={async () => {
                      try {
                        toast.success('Análise de avanço automático iniciada');
                        fetchData(); // Refresh data
                      } catch (error) {
                        toast.error('Erro ao executar avanço automático');
                      }
                    }}
                  >
                    <Ico e="🚀" />Executar Avanços
                  </Button>
                </div>

                <div className="p-6 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-orange-200">
                  <div className="flex items-center space-x-3 mb-4">
                    <span className="text-2xl"><Ico e="⚠" bare /></span>
                    <div>
                      <h3 className="font-semibold text-slate-900">Detecção de Riscos</h3>
                      <p className="text-sm text-slate-600">Identificação proativa de problemas</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-700 mb-4">
                    Sistema inteligente que identifica negócios em risco e sugere ações preventivas.
                  </p>
                  <Button 
                    className="w-full bg-orange-600 hover:bg-orange-700"
                    onClick={fetchData}
                  >
                    <Ico e="🔍" />Analisar Riscos
                  </Button>
                </div>

                <div className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                  <div className="flex items-center space-x-3 mb-4">
                    <span className="text-2xl"><Ico e="📈" bare /></span>
                    <div>
                      <h3 className="font-semibold text-slate-900">Otimização de Performance</h3>
                      <p className="text-sm text-slate-600">Sugestões de melhoria contínua</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-700 mb-4">
                    Análise de dados históricos para identificar oportunidades de otimização no processo.
                  </p>
                  <Button 
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    onClick={() => toast.info('Funcionalidade em desenvolvimento')}
                  >
                    <Ico e="📊" />Gerar Relatório
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CONCORRÊNCIA */}
        <TabsContent value="concorrencia" className="space-y-6 mt-6">
          <CompetitorAnalysis api={api} />
        </TabsContent>

        {/* RADAR DE RISCO */}
        <TabsContent value="radar" className="space-y-6 mt-6">
          <RiskRadar rottingDeals={rottingDeals} alerts={alerts} insights={insights} />
        </TabsContent>

      </Tabs>

      {/* Deal Score Modal */}
      {dealScore && (
        <Dialog open={showScoreModal} onOpenChange={setShowScoreModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <span><Ico e="📊" bare /></span>
                <span>Score do Negócio</span>
              </DialogTitle>
              <DialogDescription>
                Análise detalhada de probabilidade e qualificação
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              <div className="text-center p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
                <p className="text-4xl font-bold text-purple-600 mb-2">
                  {dealScore.score.toFixed(0)}
                </p>
                <p className="text-slate-600">Score Geral (0-100)</p>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-semibold text-slate-900">Fatores de Análise:</h4>
                {Object.entries(dealScore.factors).map(([factor, score]) => (
                  <div key={factor} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm font-medium capitalize">
                      {factor.replace('_', ' ')}
                    </span>
                    <div className="flex items-center space-x-2">
                      <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-orange-500 to-purple-600 rounded-full"
                          style={{ width: `${(score / 25) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-bold text-purple-600">
                        {score.toFixed(1)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Recomendações:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  {dealScore.score >= 80 && <li>• Negócio altamente qualificado - priorize o fechamento</li>}
                  {dealScore.score >= 60 && dealScore.score < 80 && <li>• Bom potencial - continue engajamento ativo</li>}
                  {dealScore.score >= 40 && dealScore.score < 60 && <li>• Precisa de mais qualificação e atividades</li>}
                  {dealScore.score < 40 && <li>• Baixa probabilidade - reavalie critérios de qualificação</li>}
                  {dealScore.factors.recency < 8 && <li>• Aumente frequência de contato</li>}
                  {dealScore.factors.activity_engagement < 10 && <li>• Crie mais atividades de engajamento</li>}
                </ul>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowScoreModal(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ── Análise de Concorrência ─────────────────────────────────────────────────
function CompetitorAnalysis({ api }) {
  const [lostDeals, setLostDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/deals?status=lost&limit=200')
      .then(r => {
        const deals = Array.isArray(r.data) ? r.data : r.data?.deals || r.data?.items || [];
        setLostDeals(deals);
      })
      .catch(() => setLostDeals([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-12 text-gray-400">Carregando análise de concorrência...</div>;

  // Aggregate competitors
  const competitorMap = {};
  let withCompetitor = 0;
  let withoutCompetitor = 0;
  lostDeals.forEach(d => {
    const comp = d.lost_to_competitor;
    if (comp) {
      withCompetitor++;
      competitorMap[comp] = (competitorMap[comp] || 0) + 1;
    } else {
      withoutCompetitor++;
    }
  });
  const sorted = Object.entries(competitorMap).sort((a, b) => b[1] - a[1]);
  const maxCount = sorted[0]?.[1] || 1;

  // Win/loss reasons
  const reasonMap = {};
  lostDeals.forEach(d => {
    const reason = d.lost_reason || 'Não especificado';
    reasonMap[reason] = (reasonMap[reason] || 0) + 1;
  });
  const sortedReasons = Object.entries(reasonMap).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-red-600">{lostDeals.length}</div>
            <div className="text-sm text-gray-500 mt-1">Negócios Perdidos</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-orange-600">{sorted.length}</div>
            <div className="text-sm text-gray-500 mt-1">Concorrentes Identificados</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-blue-600">{lostDeals.length > 0 ? Math.round((withCompetitor / lostDeals.length) * 100) : 0}%</div>
            <div className="text-sm text-gray-500 mt-1">Com Concorrente Mapeado</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Competitors ranking */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><span><Ico e="⚔" bare /></span> Concorrentes que Ganham</CardTitle>
            <CardDescription>Negócios perdidos por concorrente</CardDescription>
          </CardHeader>
          <CardContent>
            {sorted.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <div className="text-3xl mb-2"><Ico e="📊" bare /></div>
                <p className="text-sm">Nenhum concorrente registrado ainda.</p>
                <p className="text-xs mt-1">Ao marcar negócios como perdidos, registre o concorrente para ver análises aqui.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sorted.map(([name, count], idx) => (
                  <div key={name} className="space-y-1">
                    <div className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${idx === 0 ? 'bg-red-100 text-red-700' : idx === 1 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}>#{idx+1}</span>
                        <span className="font-medium">{name}</span>
                      </div>
                      <span className="text-gray-500 font-medium">{count} {count === 1 ? 'vez' : 'vezes'}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className="bg-red-500 h-2 rounded-full transition-all" style={{ width: `${(count/maxCount)*100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Loss reasons */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><span><Ico e="📋" bare /></span> Motivos de Perda</CardTitle>
            <CardDescription>Distribuição dos motivos registrados</CardDescription>
          </CardHeader>
          <CardContent>
            {sortedReasons.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">Nenhum negócio perdido registrado ainda.</div>
            ) : (
              <div className="space-y-3">
                {sortedReasons.slice(0, 6).map(([reason, count]) => (
                  <div key={reason} className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-700">{reason}</span>
                    <Badge className="bg-red-100 text-red-700">{count}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Lost deals list with competitor */}
      {lostDeals.filter(d => d.lost_to_competitor).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><span><Ico e="📜" bare /></span> Histórico de Perdas por Concorrente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {lostDeals.filter(d => d.lost_to_competitor).map(deal => (
                <div key={deal.id} className="flex justify-between items-center p-3 border rounded-lg bg-red-50/40">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{deal.title}</p>
                    <p className="text-xs text-gray-500">{deal.lost_reason || 'Motivo não especificado'}</p>
                  </div>
                  <div className="text-right">
                    <Badge className="bg-red-100 text-red-700 text-xs">{deal.lost_to_competitor}</Badge>
                    <p className="text-xs text-gray-400 mt-1">R$ {(deal.value||0).toLocaleString('pt-BR')}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Radar de Risco ──────────────────────────────────────────────────────────
function RiskRadar({ rottingDeals, alerts, insights }) {
  const deals = rottingDeals?.deals || [];
  const critical = deals.filter(d => d.rot_score >= 80 || d.days_without_activity >= 30);
  const high = deals.filter(d => (d.rot_score >= 50 && d.rot_score < 80) || (d.days_without_activity >= 21 && d.days_without_activity < 30));
  const medium = deals.filter(d => d.rot_score < 50 && d.days_without_activity < 21);

  const getRiskBadge = (deal) => {
    if (deal.rot_score >= 80 || deal.days_without_activity >= 30) return { label: <><Ico e="🔴" />Crítico</>, cls: 'bg-red-100 text-red-800' };
    if (deal.rot_score >= 50 || deal.days_without_activity >= 21) return { label: <><Ico e="🟠" />Alto</>, cls: 'bg-orange-100 text-orange-800' };
    return { label: <><Ico e="🟡" />Médio</>, cls: 'bg-yellow-100 text-yellow-800' };
  };

  return (
    <div className="space-y-6">
      {/* Risk summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-red-600">{critical.length}</div>
            <div className="text-sm text-red-700 mt-1 font-medium"><Ico e="🔴" />Risco Crítico</div>
            <div className="text-xs text-red-500 mt-1">+30 dias sem atividade</div>
          </CardContent>
        </Card>
        <Card className="border-orange-200 bg-orange-50/50">
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-orange-600">{high.length}</div>
            <div className="text-sm text-orange-700 mt-1 font-medium"><Ico e="🟠" />Risco Alto</div>
            <div className="text-xs text-orange-500 mt-1">21-30 dias sem atividade</div>
          </CardContent>
        </Card>
        <Card className="border-yellow-200 bg-yellow-50/50">
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-yellow-600">{medium.length}</div>
            <div className="text-sm text-yellow-700 mt-1 font-medium"><Ico e="🟡" />Risco Médio</div>
            <div className="text-xs text-yellow-500 mt-1">14-20 dias sem atividade</div>
          </CardContent>
        </Card>
      </div>

      {/* Risk table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span><Ico e="🎯" bare /></span> Negócios em Radar de Risco
            <Badge className="ml-2 bg-gray-100 text-gray-700">{deals.length} negócios</Badge>
          </CardTitle>
          <CardDescription>Ordenados por nível de risco — tome ação imediata nos críticos</CardDescription>
        </CardHeader>
        <CardContent>
          {deals.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-3"><Ico e="✅" bare /></div>
              <p className="text-gray-600 font-medium">Radar limpo!</p>
              <p className="text-gray-400 text-sm mt-1">Nenhum negócio em risco detectado no momento.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {[...critical, ...high, ...medium].map(deal => {
                const risk = getRiskBadge(deal);
                const totalValue = deals.reduce((s, d) => s + (d.value || 0), 0);
                return (
                  <div key={deal.id} className="flex items-center justify-between p-4 border rounded-xl hover:shadow-sm transition-shadow">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`text-xs ${risk.cls}`}>{risk.label}</Badge>
                        <span className="text-sm font-semibold text-gray-800">{deal.title}</span>
                      </div>
                      <div className="flex gap-4 text-xs text-gray-500">
                        <span><Ico e="⏰" bare />{deal.days_without_activity || 0} dias sem atividade</span>
                        {deal.responsible_name && <span><Ico e="👤" bare />{deal.responsible_name}</span>}
                        {deal.stage && <span><Ico e="📍" bare />{deal.stage}</span>}
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-sm font-bold text-gray-700">R$ {(deal.value||0).toLocaleString('pt-BR')}</div>
                      {deal.rot_score > 0 && (
                        <div className="text-xs text-gray-400">Score: {deal.rot_score?.toFixed(0)}/100</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Total value at risk */}
      {deals.length > 0 && rottingDeals?.summary?.total_value_at_risk > 0 && (
        <Card className="bg-gradient-to-r from-red-50 to-orange-50 border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-700 font-medium"><Ico e="💰" />Valor Total em Risco</p>
                <p className="text-3xl font-bold text-red-600 mt-1">R$ {(rottingDeals.summary.total_value_at_risk || 0).toLocaleString('pt-BR')}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-red-500">Recomendação</p>
                <p className="text-sm text-red-700 font-medium mt-1">Priorize os {critical.length} negócios críticos com<br/>uma ligação ou reunião esta semana.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}